import {
  Card,
  CardBody,
  Col,
  Row,
  InputGroup,
  Input,
  Label,
  FormGroup,
  InputGroupText,
} from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import CommonCardHeader from "../../../CommonComponents/CommonCardHeader/CommonCardHeader";

// Modals
import UpdateEmpInfoModal from "../../../UiKits/Modal/Employee/UpdateEmploymentInfo";

// Services
import {
  VIEW_EMPLOYEE_QUERY_KEY,
  employeeService,
} from "../../../../Services/employeeService";

// Type
import { ViewEmploymentDetails } from "../../../../Types/Employee/EmployeeModel";
import { CRUDPermissions } from "../../../../Types/Common/Access";

interface EmployeeProfileProps {
  EmpNo: string;
  isAdmin: boolean;
  access: CRUDPermissions;
}

const EmploymentInformation: React.FC<EmployeeProfileProps> = ({
  EmpNo,
  isAdmin,
  access,
}) => {
  const formatDate = (date?: string) => {
    if (!date) return "";

    const [year, month, day] = date.split("/");

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    ).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const [formData, setFormData] = useState<ViewEmploymentDetails | null>(null);

  const { data: responseData, isLoading: isFetching } = useQuery({
    queryKey: [VIEW_EMPLOYEE_QUERY_KEY, EmpNo],
    queryFn: () => employeeService.viewEmploymentByEmpno(EmpNo),
    enabled: !!EmpNo,
  });

  useEffect(() => {
    if (responseData) {
      setFormData(responseData);
    }
  }, [responseData]);

  const titleCase = (value?: string | null) =>
    value
      ? value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
      : "-";

  return (
    <Card className="card-absolute">
      <CommonCardHeader
        headClass="bg-primary"
        tagClass="txt-light"
        icon={<i className="icofont icofont-briefcase-alt-1 me-1"></i>}
        title={"Employment Information"}
      />
      <CardBody>
        <Row>
          <Col sm="6">
            <FormGroup>
              <Label>Employee</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-social-slack txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formData?.emp_no +
                          " - " +
                          formData?.full_name?.toUpperCase() ||
                        "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>Employee Status</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-tag txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formData?.employment_status_label ||
                        "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>Employment Type</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-tag txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formData?.employment_type || "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>
                {isFetching
                  ? "Loading data..."
                  : titleCase(formData?.employment_type) || "Effectivity"}{" "}
                Date
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-ui-calendar txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formatDate(formData?.effectivity_date)?.toUpperCase() ||
                        "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>Hired Date</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-ui-calendar txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formatDate(formData?.hired_date)?.toUpperCase() ||
                        "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>Start Date</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-ui-calendar txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formatDate(formData?.start_date)?.toUpperCase() ||
                        "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>End Date</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-ui-calendar txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formatDate(formData?.end_date)?.toUpperCase() || " - "
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          <Col sm="6">
            <FormGroup>
              <Label>Years of Service</Label>
              <InputGroup>
                <InputGroupText>
                  <i className="icofont icofont-briefcase-alt-1 txt-primary"></i>
                </InputGroupText>
                <Input
                  className="bg-white text-color-light"
                  disabled
                  value={
                    isFetching
                      ? "Loading data..."
                      : formData?.length_of_service || "* No Record Found *"
                  }
                />
              </InputGroup>
            </FormGroup>
          </Col>
          {isAdmin && access.update && <UpdateEmpInfoModal EmpNo={EmpNo} />}
        </Row>
      </CardBody>
    </Card>
  );
};

export default EmploymentInformation;
